import * as React from 'react';
import { render } from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useDynamicReducer } from '@micro-frontend-react/employee-experience/lib/useDynamicReducer';
import type { IUser } from '@micro-frontend-react/employee-experience/lib/IUser';

import { CoherenceThemeProvider } from './Components/CoherenceThemeProvider/CoherenceThemeProvider';
import { useNavConfig } from './navConfig';
import { Header } from './Components/Header';
import { Navigation } from './Components/Nav';
import { Routes } from './Routes';
import { ShellWithStore } from './ShellWithStore';
import { useHeaderConfig } from './useHeaderConfig';
import { LoadingSpinner } from './Components/LoadingSpinner/LoadingSpinner';
import { AccessRestricted, ContactSupportLink } from './Components/AccessRestricted/AccessRestricted';
import { Main } from './Components/Main';
import { useLoginOnStartup } from './Environments/Services/useLoginOnStartup';
import { securityGroupsReducer, securityGroupsReducerName } from './Redux/SecurityGroups/reducers';
import { requestSecurityGroupsSagas } from './Services/SecurityGroupsService';
import { requestSecurityGroups } from './Redux/SecurityGroups/actions';
import type { ISecurityGroupsState } from './Redux/SecurityGroups/types';
import { featureTreeReducer, featureTreeReducerName } from './Redux/FeatureTree/reducers';
import { requestFeatureTreeSagas } from './Services/FeatureTreeService';
import { requestFeatureTree } from './Redux/FeatureTree/actions';
import type { IFeatureTreeState } from './Redux/FeatureTree/types';
import { getAccessGateRestriction, isAccessGatePending } from './accessGate';

/** Non-technical provisioning subtext for the no-role screen, with the contact link inline. */
const NO_ACCESS_MESSAGE = (
  <>
    You&apos;re not currently provisioned for this application.{' '}
    <ContactSupportLink>Contact support</ContactSupportLink> to request access.
  </>
);

const ACCESS_CHECK_ERROR_MESSAGE =
  'We could not verify your access. Reload the page to try again.';

function App(): React.ReactElement {
  const APP_NAME = 'Performance Management';
  const [loggedInUser] = useLoginOnStartup() as [IUser | null];
  const headerConfig = useHeaderConfig();
  const [isNavCollapsed, setIsNavCollapsed] = React.useState<boolean>(false);
  const dispatch = useDispatch();

  useDynamicReducer(securityGroupsReducerName, securityGroupsReducer, [requestSecurityGroupsSagas]);
  useDynamicReducer(featureTreeReducerName, featureTreeReducer, [requestFeatureTreeSagas]);

  const securityGroupsState = useSelector(
    (state: { dynamic?: Record<string, unknown> }) => (state?.dynamic?.[securityGroupsReducerName]) as ISecurityGroupsState | undefined
  );
  const securityGroups = securityGroupsState?.groups ?? null;

  const featureTreeState = useSelector(
    (state: { dynamic?: Record<string, unknown> }) => (state?.dynamic?.[featureTreeReducerName]) as IFeatureTreeState | undefined
  );
  const isGateNotResolved = isAccessGatePending(securityGroupsState, featureTreeState);
  const accessGateRestriction = getAccessGateRestriction(
    securityGroupsState,
    featureTreeState
  );

  React.useEffect(() => {
    if (loggedInUser?.email) {
      dispatch(requestSecurityGroups());
    }
  }, [loggedInUser?.email, dispatch]);

  React.useEffect(() => {
    if (securityGroups && securityGroups.length > 0) {
      // Resolve role/country + feature tree from the user's Entra security group IDs (GUIDs).
      // Fire-and-forget: app renders immediately, gating reveals as the tree loads.
      dispatch(requestFeatureTree(securityGroups));
    }
  }, [securityGroups, dispatch]);

  const navConfigResult = useNavConfig();
  const pathname = window.location.pathname;
  const navConfig = React.useMemo(
    () => [...navConfigResult.securityNavConfig, ...navConfigResult.pbtNavConfig],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, navConfigResult.securityNavConfig, navConfigResult.pbtNavConfig]
  );

  const handleNavCollapsed = React.useCallback((isCollapsed?: boolean) => {
    if (isCollapsed !== undefined && isCollapsed !== null) {
      setIsNavCollapsed(isCollapsed);
    }
  }, []);

  React.useEffect(() => {
    if (!document.title) {
      document.title = APP_NAME;
    }
  }, []);

  if (!loggedInUser) {
    return <LoadingSpinner />;
  }

  // Hold on the spinner until the access outcome is resolved, so neither the geo nor the no-role
  // restricted screen is preceded by a flash of the app shell.
  if (isGateNotResolved) {
    return <LoadingSpinner />;
  }

  if (!navConfig) {
    return <LoadingSpinner />;
  }

  if (accessGateRestriction !== null) {
    const restrictedTitle =
      accessGateRestriction === 'role'
        ? 'Access Not Available'
        : accessGateRestriction === 'geo'
          ? 'Access Not Available for your location'
          : 'Unable to Verify Access';
    const restrictedMessage =
      accessGateRestriction === 'role'
        ? NO_ACCESS_MESSAGE
        : accessGateRestriction === 'error'
          ? ACCESS_CHECK_ERROR_MESSAGE
          : undefined;

    return (
      <CoherenceThemeProvider>
        <div>
          <Header {...headerConfig} showNotifications={false} />
          <AccessRestricted title={restrictedTitle} message={restrictedMessage} />
        </div>
      </CoherenceThemeProvider>
    );
  }

  return (
    <CoherenceThemeProvider>
      <div>
        <Header
          {...headerConfig}
        />
        <Navigation
          groups={navConfig}
          appName={APP_NAME}
          defaultIsNavCollapsed={isNavCollapsed}
          onNavCollapsed={handleNavCollapsed}
        />
        <Main
          className={isNavCollapsed ? 'navCollapsed' : 'navExpanded'}
          id="main"
          tabIndex={-1}
        >
          <Routes />
        </Main>
      </div>
    </CoherenceThemeProvider>
  );
}
render(
  // @ts-ignore
  <ShellWithStore>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </ShellWithStore>,
  document.getElementById('app')
);
